// App shell (docs/app.md §2). A single page: the header across the top, the
// selected step's queue below. The step timeline (pipeline) lives in a modal
// dialog off the header's Pipeline button; its node count badges are the
// at-a-glance KPI, and the Analytics page holds the full metrics view. Routing
// is by role: only the nodes the user can act on are lit, and selecting one
// loads that screen. The API enforces access independently of what is shown, so
// lighting a node is a hint and never a permission.
//
// The workspace level is kept even though Invoices is the only workspace: the
// state, the label map and the per-workspace role projection are the mechanism a
// second document class rides on later. What is not kept is a one-option toggle.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Body1,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Tab,
  TabList,
  Tooltip,
  Toaster,
  makeStaticStyles,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowClockwiseRegular,
  ArrowUploadRegular,
  BoardRegular,
  BookDatabaseRegular,
  DismissRegular,
  InfoRegular,
  MailRegular,
  PeopleTeamRegular,
  ReceiptRegular,
  SettingsRegular,
  TimelineRegular,
} from '@fluentui/react-icons'
import {
  TIMELINE_NODES,
  TOASTER_ID,
  DecisionStep,
  InvoiceStatus,
  WORKSPACE_LABEL,
  type NodeKey,
  type Role,
  type Workspace,
} from './lib/constants'
import { loadSession, scopeSession, availableWorkspaces, hasRole, canOpenAssignments, canOpenAnalytics, canPreviewPdf, landingPage, type Session } from './lib/identity'
import { loadQueueRows, loadStuckNodes, type StuckNode } from './lib/data'
import { applyFilters, emptyFilter, type InvoiceFilter } from './lib/filters'
import type { Invoice } from './lib/types'
import { PAGE_ID, TOPBAR_ID } from './lib/overlay'
import { Timeline } from './components/Timeline'
import { PIPELINE_DIALOG_MAX_W } from './lib/timelineLayout'
import { FilterButton, type FilterOption } from './components/FilterButton'
import { ImportControlScreen } from './screens/ImportControlScreen'
import { ApArApprovalScreen } from './screens/ApArApprovalScreen'
import { ApprovalScreen } from './screens/ApprovalScreen'
import { ExportControlScreen } from './screens/ExportControlScreen'
import { ManualEntryScreen } from './screens/ManualEntryScreen'
import { ClosingConfirmationScreen } from './screens/ClosingConfirmationScreen'
import { DenialReviewScreen } from './screens/DenialReviewScreen'
import { ArchivesScreen } from './screens/ArchivesScreen'
import { AssignmentsScreen } from './screens/AssignmentsScreen'
import { TrainingScreen } from './screens/TrainingScreen'
import { RulesScreen } from './screens/RulesScreen'
import { AnalyticsScreen } from './screens/AnalyticsScreen'
import { UploadScreen } from './screens/UploadScreen'
import { RemindersScreen } from './screens/RemindersScreen'
import { APP_VERSION } from './version'

// The main-path + branch step roles (everything but the archive lookup).
const PIPELINE_ROLES: Role[] = ['ImportControl', 'ApArApproval', 'DepartmentApproval', 'DirectorApproval', 'OversightApproval', 'ExportControl', 'ManualEntry', 'ClosingConfirmation', 'DenialReview']
const BADGE_POLL_MS = 90_000
// The user guide, shipped as a static asset beside index.html (app/public/).
// Root-relative on purpose: the SPA is served from the distribution root in
// every environment, so this needs no runtime configuration.
const GUIDE_URL = '/user-guide.pdf'

// Navigation-tab values. Analytics / References / Rules / Assignments map to
// their page keys; Invoices maps to the workspace.
type NavValue = 'invoices' | 'analytics' | 'training' | 'rules' | 'assignments'

/** Sorted, de-duplicated filter options built from the rows behind the badges.
    Carries the id as well as the name, because the API filters on the id and a
    company may be renamed. */
function optionsFrom(pairs: { id?: string | null; name?: string | null }[]): FilterOption[] {
  const byName = new Map<string, FilterOption>()
  for (const p of pairs) {
    const name = p.name ?? ''
    if (!name || byName.has(name)) continue
    byName.set(name, { id: p.id ?? '', name })
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name))
}

// The lit set drives which timeline nodes the user can open. Archives is a
// read-only view of terminal invoices reachable by anyone who works any
// pipeline step — Dataverse RLS scopes the rows (a company approver sees only
// their own company's history; internal roles see all), so lighting it broadly
// is safe. A dedicated Archives-team member (the 'Archives' role) lights it
// too. See questions.md → Archives (Option A) and app.md §7.
function litRolesFor(session: Session): Set<Role> {
  // No fail-open branch: the profile either loaded or the shell showed an error,
  // so an empty role set means the user really holds no step.
  const base: Set<Role> = new Set(session.roles)
  if (PIPELINE_ROLES.some((r) => base.has(r))) base.add('Archives')
  return base
}

// Fluent styles TableHeaderCell's inner button with `resize: horizontal`, which
// paints a native drag grip in the corner of every column header. It is there
// for Fluent's own column-sizing feature (useTableColumnSizing) — which this app
// doesn't use anywhere, and whose column widths are fixed in code. So the grip
// resized only the header's inner div: drag it and the label clipped to
// "DESCRIP" while the column stayed put (user note 2026-08-02). Turn it off
// everywhere rather than per table, so a new table can't reintroduce it.
// The doubled class is deliberate: Fluent's own `resize: horizontal` is an
// atomic single-class rule in the same style bucket, so at equal specificity it
// wins on source order. Doubling makes this rule more specific instead.
const useGlobalTableStyles = makeStaticStyles({
  '.fui-TableHeaderCell__button.fui-TableHeaderCell__button': { resize: 'none' },
})

const useStyles = makeStyles({
  page: { maxWidth: '1240px', margin: '0 auto', padding: '20px 28px 64px' },
  // Quiet enough to ignore, findable when someone is reporting a problem.
  versionLine: {
    marginTop: '24px',
    textAlign: 'right',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    userSelect: 'text',
  },
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    // Wrap the tool cluster below the nav on very narrow viewports rather than
    // letting the header overflow (the spill-out the redesign fixes).
    rowGap: '8px',
    flexWrap: 'wrap',
    paddingBottom: '12px',
    marginBottom: '24px',
    // Blue accent line that separates the header from the page body.
    borderBottom: `3px solid ${tokens.colorBrandStroke1}`,
  },
  brandRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  // The app title as a wordmark (app.md §7): brand glyph + small
  // full-contrast text. The app name is static chrome, so it stays small and
  // each page's title is the page's only large heading — but full contrast,
  // not muted (a faint gray first cut read as faded, revised same day).
  brandGlyph: { display: 'flex', fontSize: '20px', color: tokens.colorBrandForeground1 },
  wordmark: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
  // Left zone: brand wordmark + the navigation tabs (top-level sections).
  navZone: { display: 'flex', alignItems: 'center', gap: '20px', minWidth: 0 },
  // Right zone: the same-screen tools as compact icon buttons — deliberately
  // distinct from the labeled nav tabs (header redesign 2026-07-14).
  actions: { display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 },
  // Breathing room around the timeline inside the Pipeline dialog.
  // `overflowX: 'auto'`: below ~1186px of viewport the timeline's fixed-width
  // nodes cannot compress (`lib/timelineLayout.ts`'s `timelineMinWidth`), so
  // below that floor the STRIP scrolls instead of the whole dialog body — the
  // title, the close button and the muted explanation line stay put. This
  // also makes `overflow-y` compute from `visible` to `auto` (CSS Overflow 3:
  // one axis non-`visible` forces the other to `auto`) — the same rule that
  // caused the original horizontal-scroll bug — but it cannot bite here: this
  // box's height is `auto`, so it grows to its content, and at NODE_H 84 no
  // label ever spills out of its button, so there is nothing to overflow
  // vertically in the first place.
  pipelineBody: { padding: '12px 4px 8px', overflowX: 'auto' },
  pipelineSub: { display: 'block', color: tokens.colorNeutralForeground3, marginTop: '4px' },
  content: { marginTop: '16px' },
  loading: { display: 'flex', justifyContent: 'center', padding: '96px' },
  noRole: { padding: '48px 0', color: tokens.colorNeutralForeground3 },
})

function firstLitNode(litRoles: Set<Role>): NodeKey | null {
  for (const n of TIMELINE_NODES) {
    if (litRoles.has(n.role)) return n.key
  }
  return null
}

export default function App() {
  useGlobalTableStyles()
  const s = useStyles()
  const [session, setSession] = useState<Session | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [selected, setSelected] = useState<NodeKey | 'dashboard' | 'training' | 'rules' | 'assignments' | null>(null)
  // Minimal rows per timeline node; badge counts derive from these so the
  // shared queue filter applies to the whole page (counts + queues together).
  const [nodeRows, setNodeRows] = useState<Record<string, Invoice[]>>({})
  // One filter shared by every step queue and the timeline badges, so filtering
  // to a company narrows the badges and the queues together.
  const [filter, setFilter] = useState<InvoiceFilter>(emptyFilter)
  // Active workspace. A user's roles are per-workspace; the scoped session
  // drives which timeline nodes are lit.
  const [workspace, setWorkspace] = useState<Workspace>('invoice')
  const litRef = useRef<Set<Role>>(new Set())
  // Last timeline node the user worked, so Processing returns them to it.
  const lastNodeRef = useRef<NodeKey | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  // Reminders: self-service email-reminder subscriptions in a modal (reminders.md).
  const [remindersOpen, setRemindersOpen] = useState(false)
  // Pipeline: shows the step timeline in a modal dialog (Upload's pattern).
  // Selecting a node closes it and loads that step's queue.
  const [pipelineOpen, setPipelineOpen] = useState(false)
  // Bumped by the single top-bar Refresh; passed to screens so their queues
  // reload in lockstep with the timeline counts (one button refreshes all).
  const [refreshNonce, setRefreshNonce] = useState(0)
  // The Pipeline dialog's warning-icon diagnostic (§2.2 `GET /queues/stuck`).
  // Fetched only while the dialog is open — never on the 90s badge poll,
  // which is why this lives beside `pipelineOpen` rather than `refreshStats`.
  const [stuck, setStuck] = useState<Partial<Record<string, StuckNode>>>({})

  // ---- session ----
  useEffect(() => {
    loadSession()
      .then((sess) => {
        setSession(sess)
        const wss = availableWorkspaces(sess)
        const initialWs: Workspace = wss.includes('invoice') || wss.length === 0 ? 'invoice' : wss[0]
        setWorkspace(initialWs)
        const lit = litRolesFor(scopeSession(sess, initialWs))
        litRef.current = lit
        const first = firstLitNode(lit)
        setSelected(first ?? landingPage(sess))
      })
      .catch((e: Error) => setSessionError(e.message))
  }, [])

  // Session scoped to the active workspace (roles/teamIds point at that
  // workspace's teams) — drives lit nodes and the invoice screens' canDecide.
  const scoped = useMemo<Session | null>(() => (session ? scopeSession(session, workspace) : null), [session, workspace])
  const litRoles = useMemo<Set<Role>>(() => (scoped ? litRolesFor(scoped) : new Set()), [scoped])

  // ---- badge counts (lit nodes only) + light auto-refresh ----
  const refreshStats = useCallback(async () => {
    const lit = litRoles
    // Skip the archive node — its queue is unbounded (the screen paginates),
    // so it carries no waiting-count badge.
    const nodes = TIMELINE_NODES.filter((n) => lit.has(n.role) && !n.archive)
    const next: Record<string, Invoice[]> = {}
    await Promise.all(
      nodes.map(async (n) => {
        try {
          next[n.key] = await loadQueueRows(n.queueStatuses)
        } catch {
          /* leave the node's rows as-is on a transient error */
        }
      }),
    )
    setNodeRows((prev) => ({ ...prev, ...next }))
  }, [litRoles])

  // Counts recompute locally when the filter changes — no refetch needed. The
  // loaded rows run through the same filter the queues use, so a badge and its
  // queue can never disagree.
  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const [key, rows] of Object.entries(nodeRows)) c[key] = applyFilters(rows, filter).length
    return c
  }, [nodeRows, filter])

  // All rows behind the badges — these feed the Filter button's options, which
  // is why the filter never offers the whole ~2,700-row vendor master.
  const allNodeRows = useMemo(() => Object.values(nodeRows).flat(), [nodeRows])
  const filterCompanies = useMemo(
    () => optionsFrom(allNodeRows.map((i) => ({ id: i.company_id, name: i.company_name }))),
    [allNodeRows],
  )
  const filterVendors = useMemo(
    () => optionsFrom(allNodeRows.map((i) => ({ id: i.vendor_id, name: i.vendor_name }))),
    [allNodeRows],
  )

  // One refresh to rule them all: timeline counts + the active screen's queue.
  const refreshAll = useCallback(() => {
    refreshStats()
    setRefreshNonce((n) => n + 1)
  }, [refreshStats])

  useEffect(() => {
    if (!session) return
    refreshStats()
    const id = window.setInterval(refreshStats, BADGE_POLL_MS)
    return () => window.clearInterval(id)
  }, [session, refreshStats])

  // The stuck-tier diagnostic: requested when the Pipeline dialog opens, and
  // again on the top-bar Refresh while it stays open — never on the badge
  // poll above, which is why this is its own effect rather than folded into
  // `refreshStats`. A rejection clears it rather than leaving a stale warning.
  useEffect(() => {
    if (!pipelineOpen) return
    let cancelled = false
    loadStuckNodes()
      .then((res) => {
        if (!cancelled) setStuck(res)
      })
      .catch(() => {
        if (!cancelled) setStuck({})
      })
    return () => {
      cancelled = true
    }
  }, [pipelineOpen, refreshNonce])

  // Remember the last timeline node the user worked, so Processing returns to it.
  useEffect(() => {
    if (
      selected !== null &&
      selected !== 'dashboard' &&
      selected !== 'training' &&
      selected !== 'rules' &&
      selected !== 'assignments'
    )
      lastNodeRef.current = selected
  }, [selected])

  // ---- render ----
  if (sessionError) {
    return (
      <div className={s.page}>
        <MessageBar intent="error"><MessageBarBody>Could not start the app: {sessionError}</MessageBarBody></MessageBar>
      </div>
    )
  }
  if (!session) {
    return <div className={s.loading}><Spinner label="Signing in…" /></div>
  }

  const isAdmin = hasRole(session, 'Admin')
  // Fleet maintainers reach the References screen but see only its Vehicles
  // manager: fleet maintenance confers no invoice access at all, so the other
  // lists would only refuse them.
  const isFleet = hasRole(session, 'FleetManagement')
  const isTraining = hasRole(session, 'Training')
  // `canManageRules` (the API gate these screens reach) accepts `rules` OR
  // `references` so nobody loses a capability at cutover (docs/security.md,
  // docs/api.md) — the UI must match, or a `references`-only holder keeps the
  // endpoints but loses the tab and screen that reach them. This is the UI
  // half of that same transition rule; it comes out when the transition ends.
  const isRules = hasRole(session, 'Rules') || hasRole(session, 'Training')
  // Assignments tab: one gate, `canOpenAssignments` (identity.ts), used here
  // for the nav tab, in `landingPage` for the startup screen, and again as
  // the render guard below — the same question asked once rather than three
  // sites each risking a partial version of the AND it depends on.
  const canAssign = canOpenAssignments(session)
  // Analytics tab: same pattern, `canOpenAnalytics` (identity.ts) — owner item
  // 12, 2026-08-13. The tab used to be ungated; it is now the new `analytics`
  // role, full stop.
  const canSeeAnalytics = canOpenAnalytics(session)
  // Document (PDF) preview, DERIVED from the profile rather than hard-coded per
  // tier — the same question `ArchivesScreen` asks. It used to be `false` on
  // Department and Director and `true` on Oversight, which was the policy until
  // 2026-08-10; owner ruling R2 (issue 2) granted the pane to all three tiers and
  // `canPreviewPdf` in authority.ts was widened to match — but these three
  // literals were not, so the capability existed everywhere except the screen
  // that shows it. Deriving it means the next policy change moves one function,
  // not three call sites.
  const canSeePdf = canPreviewPdf(session)
  const user = session.user
  const hasAnyLit = litRoles.size > 0
  // Which workspaces the user can act in; drives the workspace tab.
  const wss = availableWorkspaces(session)
  // Switch workspace and land on that workspace's first actionable node. The
  // filter clears across the switch: the vendor option sets would not overlap,
  // so a value carried over could silently empty every queue.
  const switchWorkspace = (ws: Workspace) => {
    setWorkspace(ws)
    setFilter(emptyFilter)
    const lit = litRolesFor(scopeSession(session, ws))
    setSelected(firstLitNode(lit) ?? null)
  }
  // The nav tab that reflects the current view: a page key (assignments/
  // analytics/training/rules) wins; otherwise any pipeline node maps to its
  // workspace tab.
  const navValue: NavValue =
    selected === 'assignments'
      ? 'assignments'
      : selected === 'training'
        ? 'training'
        : selected === 'rules'
          ? 'rules'
          : selected === 'dashboard'
            ? 'analytics'
            : 'invoices'
  // True on a pipeline queue — i.e. not one of the off-pipeline pages. Drives
  // which header tools are shown.
  const onQueuePage =
    selected !== 'assignments' && selected !== 'training' && selected !== 'rules' && selected !== 'dashboard'
  const onNav = (v: NavValue) => {
    if (v === 'assignments') setSelected('assignments')
    else if (v === 'training') setSelected('training')
    else if (v === 'rules') setSelected('rules')
    else if (v === 'analytics') setSelected('dashboard')
    else switchWorkspace('invoice')
  }

  return (
    // The ids anchor the detail modal's overlay measurements (lib/overlay.ts).
    <div className={s.page} id={PAGE_ID}>
      <Toaster toasterId={TOASTER_ID} position="top-end" />
      <div className={s.topbar} id={TOPBAR_ID}>
        <div className={s.navZone}>
          <div className={s.brandRow}>
            <ReceiptRegular className={s.brandGlyph} />
            <span className={s.wordmark}>Accounts Payable</span>
          </div>
          {/* Navigation: the top-level sections as a tab strip, deliberately
              distinct from the same-screen tools on the right. The workspace tabs
              are rendered from the workspace list rather than hardcoded, so a
              second document class needs no change here — with one workspace the
              single tab reads as a section label, not a toggle. */}
          <TabList selectedValue={navValue} onTabSelect={(_, d) => onNav(d.value as NavValue)} aria-label="Sections">
            {wss.map((ws) => (
              <Tab key={ws} value="invoices" icon={<ReceiptRegular />}>
                {WORKSPACE_LABEL[ws]}
              </Tab>
            ))}
            {/* Owner item 12, 2026-08-13: the tab was ungated; it is now the
                `analytics` role, full stop (`canOpenAnalytics`, identity.ts). */}
            {canSeeAnalytics && (
              <Tab value="analytics" icon={<BoardRegular />}>
                Analytics
              </Tab>
            )}
            {/* Labelled "References": the training passes this screen was built
                for are retired, and all it does now is maintain the organization's
                own lists. The role token stays `training` — the label is the part
                that changed. The glyph followed suit (item 8, 2026-08-12): a
                lightbulb read as "training/ideas", which is exactly the retired
                meaning; a book with a small record-stack reads as reference data. */}
            {(isTraining || isFleet) && (
              <Tab value="training" icon={<BookDatabaseRegular />}>
                References
              </Tab>
            )}
            {/* Rules (owner item 10a, 2026-08-13): master data that decides
                invoice ROUTING, split out of References. Gated on the new
                `rules` role, the same way References is gated on Training. */}
            {isRules && (
              <Tab value="rules" icon={<SettingsRegular />}>
                Rules
              </Tab>
            )}
            {canAssign && (
              <Tab value="assignments" icon={<PeopleTeamRegular />}>
                Assignments
              </Tab>
            )}
          </TabList>
        </div>
        {/* Same-screen tools: compact icon buttons with tooltips. Reminders,
            Filter, Refresh and Pipeline all act on a queue, so they are hidden on
            the off-pipeline pages (Analytics / Training / Rules / Assignments)
            where there is no queue to filter, refresh or navigate away from
            (user note 2026-08-03). Upload stays everywhere — a document can arrive while
            you're anywhere in the app. Pipeline is deliberately last in the
            cluster (owner request) — the JSX order below must match this
            comment or the file contradicts itself three lines apart. */}
        <div className={s.actions}>
          <Tooltip content="Upload" relationship="label">
            <Button appearance={uploadOpen ? 'primary' : 'secondary'} icon={<ArrowUploadRegular />} onClick={() => setUploadOpen(true)} />
          </Tooltip>
          {/* User guide: the printable PDF walkthrough of the whole app, opened in
              a new tab so it reads beside the work rather than replacing it. It
              sits with Upload in the always-visible pair because the guide covers
              every page, not just the queues. `GUIDE_URL` is root-relative — the
              file ships as a static asset beside index.html, so it resolves the
              same locally, in dev and behind CloudFront with no configuration. */}
          <Tooltip content="User guide" relationship="label">
            <Button
              appearance="secondary"
              icon={<InfoRegular />}
              aria-label="User guide"
              onClick={() => window.open(GUIDE_URL, '_blank', 'noopener,noreferrer')}
            />
          </Tooltip>
          {onQueuePage && (
            <>
              {/* Reminders: self-service email digests. Shown to anyone with a
                  pipeline role (i.e. steps to be reminded about). */}
              {wss.length > 0 && (
                <Tooltip content="Email reminders" relationship="label">
                  <Button appearance={remindersOpen ? 'primary' : 'secondary'} icon={<MailRegular />} onClick={() => setRemindersOpen(true)} />
                </Tooltip>
              )}
              <FilterButton
                companies={filterCompanies}
                vendors={filterVendors}
                value={filter}
                onChange={setFilter}
              />
              <Tooltip content="Refresh" relationship="label">
                <Button appearance="secondary" icon={<ArrowClockwiseRegular />} onClick={refreshAll} />
              </Tooltip>
              <Tooltip content="Pipeline" relationship="label">
                <Button appearance={pipelineOpen ? 'primary' : 'secondary'} icon={<TimelineRegular />} onClick={() => setPipelineOpen(true)} />
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <div className={s.content}>
        {selected === 'assignments' ? (
          // Reachable today only via the nav tab (gated on `canAssign`) or
          // `landingPage` (gated on the same `canOpenAssignments`) — this is
          // a third, explicit check on the same question, so a future path
          // that sets `selected` to 'assignments' some other way cannot
          // render the screen unguarded.
          canAssign ? <AssignmentsScreen session={session} /> : null
        ) : selected === 'training' ? (
          <TrainingScreen fleetOnly={!isTraining && isFleet} />
        ) : selected === 'rules' ? (
          // Same third-explicit-check pattern as Assignments above: reachable
          // via the nav tab (gated on `isRules`) or landed on directly only by
          // this very state, but the render guard is repeated here on purpose.
          isRules ? <RulesScreen /> : null
        ) : selected === 'dashboard' ? (
          // Owner item 12, 2026-08-13: gated on `canSeeAnalytics`
          // (`canOpenAnalytics`), same pattern as Assignments and Rules above.
          canSeeAnalytics ? <AnalyticsScreen session={session} refreshNonce={refreshNonce} /> : null
                ) : !hasAnyLit || selected === null ? (
          <div className={s.noRole}>
            <Body1>
              You do not hold any invoice processing step yet.
              {isAdmin ? ' Use Assignments to grant yourself or someone else a step.' : ' Ask an administrator to assign you one.'}
            </Body1>
          </div>
        ) : selected === 'import-control' ? (
          <ImportControlScreen user={user} refreshNonce={refreshNonce} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'ap-ar' ? (
          // AP/AR Approval (issue 3, 2026-08-11) — mandatory, company-scoped,
          // between Import Control and the three tiers below.
          <ApArApprovalScreen session={session} refreshNonce={refreshNonce} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'department' ? (
          // Three-tier linear approval (planning §3): Department → Director →
          // Oversight. Each is a keyed ApprovalScreen instance so React doesn't
          // reuse one across a tier switch (state — holds/selection/open — would
          // leak). All three tiers show the PDF pane when the profile allows it
          // (`canSeePdf`, above): owner ruling R2 made document access a
          // capability of holding any approval seat, not a property of the tier,
          // and `docs/security.md` §5 owns the policy and its accepted exposure.
          // BOTH plain-language questions are unlocked at every approval step.
          // "Who is it for?" (editCostCenters) is the one these steps exist to
          // answer, and D23 refuses the approval until every line has one — but
          // "What is it?" (editExpenseTypes) is theirs too: the server's matrix
          // grants it at all three tiers and the half-write route has always
          // accepted it, so offering only the second half meant an approver who
          // could see a wrong expense type had no way to fix it. The COMPOSED
          // account picker is not offered here: it belongs to Export Control and
          // Manual Entry, and the server refuses it at these steps.
          <ApprovalScreen
            key="approval-department"
            session={session}
            refreshNonce={refreshNonce}
            filter={filter}
            onQueuesChanged={refreshStats}
            config={{
              title: 'Department Approval',
              subtitle: 'For each invoice, confirm the line items and their GL accounts, then approve to advance to the next approval step or deny to send for Denial Review.',
              queueStatus: InvoiceStatus.PendingDepartment,
              step: DecisionStep.Department,
              approveLabel: 'Approve',
              showPdf: canSeePdf,
              editExpenseTypes: true,
              editCostCenters: true,
            }}
          />
        ) : selected === 'director' ? (
          <ApprovalScreen
            key="approval-director"
            session={session}
            refreshNonce={refreshNonce}
            filter={filter}
            onQueuesChanged={refreshStats}
            config={{
              title: 'Director Approval',
              subtitle: 'For each invoice, approve to advance to the next approval step or deny to send for Denial Review.',
              queueStatus: InvoiceStatus.PendingDirector,
              step: DecisionStep.Director,
              approveLabel: 'Approve',
              showPdf: canSeePdf,
              editExpenseTypes: true,
              editCostCenters: true,
            }}
          />
        ) : selected === 'oversight' ? (
          <ApprovalScreen
            key="approval-oversight"
            session={session}
            refreshNonce={refreshNonce}
            filter={filter}
            onQueuesChanged={refreshStats}
            config={{
              title: 'Oversight Approval',
              subtitle: 'For each invoice, approve to release for Export Control or deny to send for Denial Review.',
              queueStatus: InvoiceStatus.PendingOversight,
              step: DecisionStep.Oversight,
              approveLabel: 'Approve',
              showPdf: canSeePdf,
              editExpenseTypes: true,
              editCostCenters: true,
            }}
          />
        ) : selected === 'export-control' ? (
          <ExportControlScreen session={session} refreshNonce={refreshNonce} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'manual-entry' ? (
          <ManualEntryScreen session={session} refreshNonce={refreshNonce} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'closing-confirmation' ? (
          <ClosingConfirmationScreen session={session} refreshNonce={refreshNonce} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'denial-review' ? (
          <DenialReviewScreen session={session} refreshNonce={refreshNonce} filter={filter} onQueuesChanged={refreshStats} />
        ) : selected === 'archives' ? (
          <ArchivesScreen session={session} refreshNonce={refreshNonce} filter={filter} />
        ) : null}
      </div>

      <div className={s.versionLine}>v{APP_VERSION}</div>

      <Dialog open={pipelineOpen} onOpenChange={(_, d) => setPipelineOpen(d.open)}>
        {/* Wide enough for the main-path timeline (Import Control → AP/AR
            Approval → Department Approval → Director Approval → Oversight
            Approval → Export Control → Closing Confirmation) plus the fork +
            branch + archive columns, with no horizontal scrollbar. The
            timeline's min-content is 1128px (`lib/timelineLayout.ts`'s
            `timelineMinWidth`); this surface leaves it 1182px after chrome —
            `pipeline-dialog-width.test.ts` holds the two together. */}
        <DialogSurface style={{ maxWidth: PIPELINE_DIALOG_MAX_W }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={() => setPipelineOpen(false)}
                />
              }
            >
              Pipeline
            </DialogTitle>
            <DialogContent>
              <Body1 className={s.pipelineSub}>
                Navigate to a particular step in the invoice processing pipeline. Steps to which you have
                access are blue; steps to which you do not have access are gray.
              </Body1>
              <div className={s.pipelineBody}>
                <Timeline
                  litRoles={litRoles}
                  selected={
                    selected === 'dashboard' ||
                    selected === 'training' ||
                    selected === 'rules' ||
                    selected === 'assignments'
                      ? null
                      : selected
                  }
                  counts={counts}
                  stuck={stuck}
                  onSelect={(k) => {
                    setSelected(k)
                    setPipelineOpen(false)
                  }}
                />
              </div>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={(_, d) => setUploadOpen(d.open)}>
        <DialogSurface style={{ maxWidth: 560 }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={() => setUploadOpen(false)}
                />
              }
            >
              Upload
            </DialogTitle>
            <DialogContent>
              <UploadScreen />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={remindersOpen} onOpenChange={(_, d) => setRemindersOpen(d.open)}>
        <DialogSurface style={{ maxWidth: 620 }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={() => setRemindersOpen(false)}
                />
              }
            >
              Email reminders
            </DialogTitle>
            <DialogContent>
              <RemindersScreen session={session} />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
